import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Fingerprint, Delete, AlertTriangle, Zap, Shield, Wifi, Eye, Activity, CheckCircle2 } from 'lucide-react';
import CONFIG from '@/config/siteConfig';
import { PASSWORD_GATE } from '@/config/missionContent';

const CORRECT_CODE = CONFIG.passwordCode;
const BG_IMG = PASSWORD_GATE.bgImage;

const BOOT_LINES = PASSWORD_GATE.bootLines;

const SYS_MESSAGES = PASSWORD_GATE.sysMessages;

/* ═══════════════════════════════════════════════
   TYPED TEXT — character-by-character reveal
   ═══════════════════════════════════════════════ */
function TypedLine({ text, speed = 20, onDone }) {
  const [displayed, setDisplayed] = useState('');
  const idx = useRef(0);

  useEffect(() => {
    if (!text) { onDone?.(); return; }
    idx.current = 0;
    setDisplayed('');
    const iv = setInterval(() => {
      idx.current++;
      setDisplayed(text.slice(0, idx.current));
      if (idx.current >= text.length) {
        clearInterval(iv);
        onDone?.();
      }
    }, speed);
    return () => clearInterval(iv);
  }, [text, speed]); // eslint-disable-line react-hooks/exhaustive-deps

  return <>{displayed}</>;
}

/* ═══════════════════════════════════════════════
   BOOT SEQUENCE — CRT power-on + typed lines
   ═══════════════════════════════════════════════ */
function BootSequence({ onComplete }) {
  const [crtPhase, setCrtPhase] = useState('off'); // off → line → expand → on
  const [lines, setLines] = useState([]);
  const [progress, setProgress] = useState(0);
  const [flashGreen, setFlashGreen] = useState(false);

  useEffect(() => {
    // CRT power-on sequence
    const t1 = setTimeout(() => setCrtPhase('line'), 300);
    const t2 = setTimeout(() => setCrtPhase('expand'), 800);
    const t3 = setTimeout(() => setCrtPhase('on'), 1200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  useEffect(() => {
    if (crtPhase !== 'on') return;
    const timers = BOOT_LINES.map((line, i) =>
      setTimeout(() => {
        setLines(prev => [...prev, line]);
        // Flash on [OK] lines
        if (line.type === 'ok') {
          setFlashGreen(true);
          setTimeout(() => setFlashGreen(false), 100);
        }
      }, line.delay)
    );
    // Progress counter
    const progTimer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) { clearInterval(progTimer); return 100; }
        return Math.min(100, prev + 2 + Math.random() * 3);
      });
    }, 80);
    const done = setTimeout(onComplete, 4500);
    return () => { timers.forEach(clearTimeout); clearTimeout(done); clearInterval(progTimer); };
  }, [crtPhase, onComplete]);

  const getLineColor = (type) => {
    switch (type) {
      case 'ascii': return 'text-red-600';
      case 'header': return 'text-green-400';
      case 'dim': return 'text-green-900';
      case 'ok': return 'text-green-500';
      case 'warn': return 'text-yellow-500';
      case 'danger': return 'text-red-500';
      case 'prompt': return 'text-green-300';
      default: return 'text-green-800';
    }
  };

  return (
    <motion.div
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
      style={{ background: '#000' }}
    >
      {/* CRT Scanlines */}
      <div className="absolute inset-0 pointer-events-none z-30" style={{
        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.15) 2px, transparent 4px)',
      }} />

      {/* CRT Power-on effect */}
      <AnimatePresence>
        {crtPhase === 'off' && (
          <motion.div className="absolute inset-0 z-40 bg-black" />
        )}
        {crtPhase === 'line' && (
          <motion.div
            initial={{ scaleY: 0, opacity: 1 }}
            animate={{ scaleY: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 z-40 flex items-center justify-center"
            style={{ background: '#000' }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '80%' }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="h-[2px]"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,65,0.8), rgba(255,255,255,0.9), rgba(0,255,65,0.8), transparent)' }}
            />
          </motion.div>
        )}
        {crtPhase === 'expand' && (
          <motion.div
            initial={{ scaleY: 0.005, opacity: 1 }}
            animate={{ scaleY: 1, opacity: [1, 1, 0.8, 1] }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="absolute inset-0 z-40"
            style={{
              background: 'radial-gradient(ellipse at center, rgba(0,255,65,0.06) 0%, rgba(0,0,0,0.95) 70%)',
              transformOrigin: 'center center',
            }}
          />
        )}
      </AnimatePresence>

      {/* Screen flicker */}
      {crtPhase === 'on' && (
        <motion.div
          animate={{ opacity: [1, 0.97, 1, 0.98, 1, 0.96, 1] }}
          transition={{ duration: 4, repeat: Infinity }}
          className="absolute inset-0 z-10"
        />
      )}

      {/* Green flash on [OK] */}
      <AnimatePresence>
        {flashGreen && (
          <motion.div
            initial={{ opacity: 0.15 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="absolute inset-0 z-20 pointer-events-none"
            style={{ background: 'rgba(0,255,65,0.04)' }}
          />
        )}
      </AnimatePresence>

      {/* Content */}
      {crtPhase === 'on' && (
        <div className="max-w-xl w-full px-8 relative z-10">
          <div className="mb-4 flex items-center gap-3">
            <img src="/umbrella-corporation-logo.png" alt="Umbrella Corp" style={{ width: 24, height: 24 }} />
            <div className="h-px flex-1" style={{ background: 'linear-gradient(to right, rgba(204,0,0,0.4), transparent)' }} />
          </div>

          {/* Terminal lines */}
          <div className="space-y-0.5 font-terminal text-sm tracking-wider" style={{ minHeight: 320 }}>
            {lines.map((line, i) => {
              if (line.type === 'blank') return <div key={i} className="h-3" />;
              const isAscii = line.type === 'ascii';
              return (
                <motion.p
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: isAscii ? 0.05 : 0.1 }}
                  className={`${getLineColor(line.type)} ${isAscii ? 'text-xs md:text-xs leading-none' : ''} whitespace-pre`}
                >
                  {isAscii ? line.text : (
                    i === lines.length - 1 && !isAscii ? (
                      <TypedLine text={line.text} speed={line.type === 'prompt' ? 30 : 15} />
                    ) : line.text
                  )}
                  {/* Highlight brackets */}
                  {line.text.includes('[OK]') && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="ml-1"
                    >
                      ✓
                    </motion.span>
                  )}
                </motion.p>
              );
            })}
            {/* Blinking cursor */}
            {lines.length > 0 && lines[lines.length - 1]?.type === 'prompt' && (
              <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.8, repeat: Infinity }}
                className="text-green-300 text-sm"
              >
                █
              </motion.span>
            )}
          </div>

          {/* Progress bar with percentage */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-terminal text-green-800 tracking-wider">{PASSWORD_GATE.boot.systemInit}</span>
              <span className="text-xs font-terminal text-green-500 tabular-nums">{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 bg-green-900/20 rounded-full overflow-hidden relative">
              <motion.div
                style={{ width: `${progress}%` }}
                className="h-full rounded-full relative overflow-hidden"
                animate={{
                  background: progress >= 100
                    ? 'linear-gradient(90deg, #16a34a, #4ade80)'
                    : 'linear-gradient(90deg, #dc2626, #16a34a, #4ade80)',
                }}
              >
                {/* Shimmer */}
                <motion.div
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)', width: '30%' }}
                />
              </motion.div>
            </div>
          </div>
        </div>
      )}

      {/* CRT screen curvature overlay */}
      <div className="absolute inset-0 pointer-events-none z-30" style={{
        background: 'radial-gradient(ellipse at center, transparent 60%, rgba(0,0,0,0.4) 100%)',
      }} />
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════
   ANIMATED HEX GRID — background atmosphere
   ═══════════════════════════════════════════════ */
function HexGrid() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 2 }}
      className="fixed inset-0 pointer-events-none z-[1] overflow-hidden"
    >
      <motion.div
        animate={{ backgroundPositionY: ['0px', '60px'] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        className="absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(30deg, rgba(204,0,0,0.03) 12%, transparent 12.5%, transparent 87%, rgba(204,0,0,0.03) 87.5%),
            linear-gradient(150deg, rgba(204,0,0,0.03) 12%, transparent 12.5%, transparent 87%, rgba(204,0,0,0.03) 87.5%),
            linear-gradient(30deg, rgba(204,0,0,0.03) 12%, transparent 12.5%, transparent 87%, rgba(204,0,0,0.03) 87.5%),
            linear-gradient(150deg, rgba(204,0,0,0.03) 12%, transparent 12.5%, transparent 87%, rgba(204,0,0,0.03) 87.5%),
            linear-gradient(60deg, rgba(0,255,65,0.02) 25%, transparent 25.5%, transparent 75%, rgba(0,255,65,0.02) 75%),
            linear-gradient(60deg, rgba(0,255,65,0.02) 25%, transparent 25.5%, transparent 75%, rgba(0,255,65,0.02) 75%)
          `,
          backgroundSize: '40px 70px',
          backgroundPosition: '0 0, 0 0, 20px 35px, 20px 35px, 0 0, 20px 35px',
        }}
      />
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════
   FLOATING PARTICLES — enhanced with sparks
   ═══════════════════════════════════════════════ */
function FloatingParticles() {
  const particles = useMemo(() =>
    Array.from({ length: 40 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: 50 + Math.random() * 50,
      yEnd: Math.random() * 40,
      size: i < 5 ? 2 + Math.random() * 3 : 1 + Math.random() * 2,
      isSpark: i < 5,
      isRed: i % 3 === 0,
      duration: 6 + Math.random() * 8,
      delay: Math.random() * 5,
    })), []
  );

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-[2]">
      {particles.map(p => (
        <motion.div
          key={p.id}
          initial={{ x: `${p.x}vw`, y: `${p.y}vh`, opacity: 0 }}
          animate={{
            y: [`${p.y}vh`, `${p.yEnd}vh`],
            opacity: [0, p.isSpark ? 0.6 : 0.2, 0],
            scale: p.isSpark ? [1, 1.5, 0] : [1, 1, 1],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            repeatDelay: Math.random() * 3,
          }}
          className="absolute"
        >
          <div
            className="rounded-full"
            style={{
              width: p.size,
              height: p.size,
              background: p.isRed ? 'rgba(204,0,0,0.5)' : 'rgba(0,255,65,0.4)',
              boxShadow: p.isSpark
                ? `0 0 ${p.size * 3}px ${p.isRed ? 'rgba(204,0,0,0.4)' : 'rgba(0,255,65,0.3)'}`
                : `0 0 4px ${p.isRed ? 'rgba(204,0,0,0.3)' : 'rgba(0,255,65,0.2)'}`,
            }}
          />
        </motion.div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SYSTEM LOG — top bar with threat level
   ═══════════════════════════════════════════════ */
function SystemLog({ attempts }) {
  const [currentMsg, setCurrentMsg] = useState(0);
  const threatLevel = Math.min(5, attempts);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMsg(prev => (prev + 1) % SYS_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 z-20">
      {/* Main bar */}
      <div className="flex items-center h-8 px-4 bg-black/85 border-b border-green-900/30 overflow-hidden backdrop-blur-sm">
        <div className="flex items-center gap-2 mr-4">
          <motion.div
            animate={{ scale: [1, 1.3, 1], opacity: [1, 0.6, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
            className="w-1.5 h-1.5 rounded-full bg-red-500"
          />
          <span className="text-xs font-terminal text-red-500 tracking-wider">{PASSWORD_GATE.hud.live}</span>
        </div>
        <AnimatePresence mode="wait">
          <motion.span
            key={currentMsg}
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -12, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="text-xs font-terminal text-green-700 tracking-wider flex-1"
          >
            SYS &gt; {SYS_MESSAGES[currentMsg]}
          </motion.span>
        </AnimatePresence>
        <span className="text-xs font-terminal text-green-900 tracking-wider tabular-nums">
          {new Date().toLocaleTimeString('en-US', { hour12: false })}
        </span>
      </div>
      {/* Threat level bar */}
      {attempts > 0 && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="flex items-center gap-2 px-4 py-1 bg-black/70 border-b border-red-900/20"
        >
          <AlertTriangle size={9} className="text-red-600" />
          <span className="text-xs font-terminal text-red-700 tracking-wider">{PASSWORD_GATE.hud.threatLevel}</span>
          <div className="flex gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <motion.div
                key={i}
                animate={i < threatLevel ? { opacity: [0.6, 1, 0.6] } : {}}
                transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.1 }}
                className="w-4 h-1.5 rounded-sm"
                style={{
                  background: i < threatLevel
                    ? i >= 3 ? '#ef4444' : i >= 1 ? '#eab308' : '#f97316'
                    : 'rgba(239,68,68,0.15)',
                }}
              />
            ))}
          </div>
          {attempts >= 3 && (
            <motion.span
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="text-xs font-terminal text-red-500 tracking-wider ml-2"
            >
              {PASSWORD_GATE.hud.intruderDetection}
            </motion.span>
          )}
        </motion.div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   HUD CORNERS — with animated elements
   ═══════════════════════════════════════════════ */
function HUDCorners() {
  return (
    <>
      {/* Corner brackets */}
      {[
        'top-10 left-2 border-t-2 border-l-2',
        'top-10 right-2 border-t-2 border-r-2',
        'bottom-2 left-2 border-b-2 border-l-2',
        'bottom-2 right-2 border-b-2 border-r-2',
      ].map((cls, i) => (
        <div key={i} className={`absolute w-8 h-8 z-20 ${cls}`}
          style={{ borderColor: `rgba(204,0,0,${i < 2 ? 0.5 : 0.3})` }}
        />
      ))}

      {/* Radar sweep — top right */}
      <div className="fixed top-12 right-3 z-20 w-12 h-12">
        <svg viewBox="0 0 50 50" className="w-full h-full">
          <circle cx="25" cy="25" r="20" fill="none" stroke="rgba(0,255,65,0.1)" strokeWidth="0.5" />
          <circle cx="25" cy="25" r="12" fill="none" stroke="rgba(0,255,65,0.07)" strokeWidth="0.5" />
          <circle cx="25" cy="25" r="4" fill="none" stroke="rgba(0,255,65,0.05)" strokeWidth="0.5" />
          <line x1="5" y1="25" x2="45" y2="25" stroke="rgba(0,255,65,0.05)" strokeWidth="0.5" />
          <line x1="25" y1="5" x2="25" y2="45" stroke="rgba(0,255,65,0.05)" strokeWidth="0.5" />
          <motion.line
            x1="25" y1="25" x2="25" y2="5"
            stroke="rgba(0,255,65,0.4)" strokeWidth="1" strokeLinecap="round"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
            style={{ transformOrigin: '25px 25px' }}
          />
          {/* Blip */}
          <motion.circle
            cx="32" cy="15" r="1.5"
            fill="#00ff41"
            animate={{ opacity: [0, 0.8, 0], scale: [1, 1.5, 1] }}
            transition={{ duration: 2, repeat: Infinity, delay: 1 }}
          />
        </svg>
      </div>

      {/* Heartbeat line — bottom left */}
      <div className="fixed bottom-10 left-3 z-20 w-20 h-6 overflow-hidden">
        <svg viewBox="0 0 80 24" className="w-full h-full">
          <motion.polyline
            points="0,12 15,12 20,4 25,20 30,8 35,16 40,12 80,12"
            fill="none"
            stroke="rgba(0,255,65,0.3)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: [0, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          />
        </svg>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════
   RED VIGNETTE PULSE
   ═══════════════════════════════════════════════ */
function RedVignette() {
  return (
    <motion.div
      animate={{ opacity: [0.3, 0.6, 0.3] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      className="fixed inset-0 pointer-events-none z-[3]"
      style={{
        background: 'radial-gradient(ellipse at center, transparent 40%, rgba(204,0,0,0.08) 80%, rgba(204,0,0,0.15) 100%)',
      }}
    />
  );
}

/* ═══════════════════════════════════════════════
   FINGERPRINT SCANNER — enhanced states
   ═══════════════════════════════════════════════ */
function FingerprintScanner({ status }) {
  return (
    <div className="relative w-24 h-24 mx-auto">
      {/* Breathing outer ring (idle) */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
        <circle cx="50" cy="50" r="44" fill="none" strokeWidth="0.5"
          stroke={status === 'granted' ? 'rgba(0,255,65,0.3)' : status === 'denied' ? 'rgba(255,0,0,0.3)' : 'rgba(204,0,0,0.15)'}
        />
        {/* Main ring */}
        <motion.circle cx="50" cy="50" r="44" fill="none" strokeWidth="1.5"
          stroke={
            status === 'scanning' ? '#eab308'
              : status === 'granted' ? '#00ff41'
                : status === 'denied' ? '#ef4444'
                  : 'rgba(204,0,0,0.2)'
          }
          strokeDasharray={status === 'scanning' ? '25 251' : '276'}
          strokeLinecap="round"
          animate={
            status === 'scanning' ? { rotate: [0, 360] }
              : status === 'idle' ? { opacity: [0.4, 0.8, 0.4] }
                : status === 'granted' ? { strokeDasharray: ['0 276', '276 0'] }
                  : {}
          }
          transition={
            status === 'scanning' ? { duration: 1.2, repeat: Infinity, ease: 'linear' }
              : status === 'idle' ? { duration: 3, repeat: Infinity, ease: 'easeInOut' }
                : status === 'granted' ? { duration: 0.6, ease: 'easeOut' }
                  : {}
          }
          style={{ transformOrigin: '50px 50px' }}
        />
        {/* Second ring for scanning */}
        {status === 'scanning' && (
          <motion.circle cx="50" cy="50" r="40" fill="none" strokeWidth="1"
            stroke="rgba(234,179,8,0.3)"
            strokeDasharray="15 240"
            strokeLinecap="round"
            animate={{ rotate: [360, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            style={{ transformOrigin: '50px 50px' }}
          />
        )}
        {/* Third ring for scanning */}
        {status === 'scanning' && (
          <motion.circle cx="50" cy="50" r="48" fill="none" strokeWidth="0.5"
            stroke="rgba(234,179,8,0.2)"
            strokeDasharray="8 270"
            strokeLinecap="round"
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            style={{ transformOrigin: '50px 50px' }}
          />
        )}
      </svg>

      {/* Shockwave on grant */}
      {status === 'granted' && (
        <>
          {[0, 0.15, 0.3].map((delay, i) => (
            <motion.div
              key={i}
              initial={{ scale: 0.5, opacity: 0.6 }}
              animate={{ scale: 2.5, opacity: 0 }}
              transition={{ duration: 0.8, delay, ease: 'easeOut' }}
              className="absolute inset-0 rounded-full"
              style={{ border: '1.5px solid rgba(0,255,65,0.4)' }}
            />
          ))}
        </>
      )}

      {/* Inner content */}
      <div className={`absolute inset-0 flex items-center justify-center transition-all duration-500
        ${status === 'granted' ? 'scale-110' : status === 'denied' ? 'scale-90' : ''}`}>
        <motion.div
          animate={status === 'idle' ? {
            boxShadow: ['0 0 15px rgba(204,0,0,0.1)', '0 0 25px rgba(204,0,0,0.2)', '0 0 15px rgba(204,0,0,0.1)'],
          } : {}}
          transition={{ duration: 3, repeat: Infinity }}
          className={`w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-500
          ${status === 'granted' ? 'bg-green-900/40 border-2 border-green-500 shadow-lg shadow-green-500/30'
              : status === 'denied' ? 'bg-red-900/40 border-2 border-red-500 shadow-lg shadow-red-500/30'
                : status === 'scanning' ? 'bg-yellow-900/20 border-2 border-yellow-600/50'
                  : 'bg-black/60 border-2 border-red-900/30'}`}
        >
          {status === 'scanning' ? (
            <motion.div
              animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <Fingerprint size={28} className="text-yellow-500" />
            </motion.div>
          ) : status === 'granted' ? (
            <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 200 }}>
              <CheckCircle2 size={28} className="text-green-400" style={{ filter: 'drop-shadow(0 0 8px rgba(0,255,65,0.5))' }} />
            </motion.div>
          ) : status === 'denied' ? (
            <motion.div animate={{ rotate: [0, -15, 15, -10, 10, 0] }} transition={{ duration: 0.5 }}>
              <AlertTriangle size={28} className="text-red-400" style={{ filter: 'drop-shadow(0 0 8px rgba(255,0,0,0.5))' }} />
            </motion.div>
          ) : (
            <Lock size={26} className="text-red-600/60" />
          )}
        </motion.div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   SCAN BAR — enhanced
   ═══════════════════════════════════════════════ */
function ScanBar({ active }) {
  if (!active) return null;
  return (
    <>
      <motion.div
        initial={{ top: '0%' }}
        animate={{ top: ['0%', '100%', '0%'] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
        className="absolute left-0 right-0 h-[2px] z-10"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(234,179,8,0.6), rgba(234,179,8,0.8), rgba(234,179,8,0.6), transparent)',
          boxShadow: '0 0 10px rgba(234,179,8,0.3), 0 0 30px rgba(234,179,8,0.1)',
        }}
      />
      {/* Second dimmer scan line */}
      <motion.div
        initial={{ top: '100%' }}
        animate={{ top: ['100%', '0%', '100%'] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'linear', delay: 0.5 }}
        className="absolute left-0 right-0 h-px z-10"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(234,179,8,0.2), transparent)' }}
      />
    </>
  );
}

/* ═══════════════════════════════════════════════
   MATRIX RAIN — brief green rain on grant
   ═══════════════════════════════════════════════ */
function MatrixRain({ active }) {
  const cols = useMemo(() =>
    Array.from({ length: 30 }, (_, i) => ({
      x: (i / 30) * 100,
      chars: Array.from({ length: 8 + Math.floor(Math.random() * 8) }, () =>
        String.fromCharCode(0x30A0 + Math.random() * 96)
      ).join(''),
      speed: 0.5 + Math.random() * 1,
      delay: Math.random() * 0.5,
      opacity: 0.15 + Math.random() * 0.25,
    })), []
  );

  if (!active) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-[35] overflow-hidden">
      {cols.map((col, i) => (
        <motion.div
          key={i}
          initial={{ y: '-100%', opacity: col.opacity }}
          animate={{ y: '110%', opacity: [col.opacity, col.opacity, 0] }}
          transition={{ duration: col.speed * 2, delay: col.delay, ease: 'linear' }}
          className="absolute top-0 text-xs leading-tight"
          style={{
            left: `${col.x}%`,
            fontFamily: 'VT323, monospace',
            color: '#00ff41',
            textShadow: '0 0 5px rgba(0,255,65,0.5)',
            writingMode: 'vertical-rl',
          }}
        >
          {col.chars}
        </motion.div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   KEYPAD RIPPLE — emanates from pressed button
   ═══════════════════════════════════════════════ */
function KeyRipple({ x, y, active }) {
  if (!active) return null;
  return (
    <motion.div
      initial={{ scale: 0, opacity: 0.5 }}
      animate={{ scale: 3, opacity: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="absolute pointer-events-none z-10"
      style={{
        left: x - 15,
        top: y - 15,
        width: 30,
        height: 30,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,255,65,0.3), transparent)',
      }}
    />
  );
}

/* ═══════════════════════════════════════════════
   MAIN PASSWORD GATE
   ═══════════════════════════════════════════════ */
export default function PasswordGate({ onComplete }) {
  const [booting, setBooting] = useState(true);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle');
  const [shaking, setShaking] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [flashScreen, setFlashScreen] = useState(null); // null | 'green' | 'red'
  const [matrixRain, setMatrixRain] = useState(false);
  const [authText, setAuthText] = useState('');
  const [lastDigitFlash, setLastDigitFlash] = useState(-1);
  const [ripple, setRipple] = useState(null);

  const keypadRef = useRef(null);

  const handleBootComplete = useCallback(() => setBooting(false), []);

  const handleKey = useCallback((digit, e) => {
    if (status === 'scanning' || status === 'granted') return;
    if (code.length < 6) {
      setCode(prev => prev + digit);
      setStatus('idle');
      setLastDigitFlash(code.length);
      setTimeout(() => setLastDigitFlash(-1), 400);

      // Ripple from button position
      if (e && keypadRef.current) {
        const rect = keypadRef.current.getBoundingClientRect();
        const btnRect = e.currentTarget.getBoundingClientRect();
        setRipple({
          x: btnRect.left - rect.left + btnRect.width / 2,
          y: btnRect.top - rect.top + btnRect.height / 2,
          id: Date.now(),
        });
        setTimeout(() => setRipple(null), 400);
      }
    }
  }, [code, status]);

  const handleDelete = useCallback(() => {
    if (status === 'scanning' || status === 'granted') return;
    setCode(prev => prev.slice(0, -1));
    setStatus('idle');
  }, [status]);

  const handleSubmit = useCallback(() => {
    if (code.length !== 6 || status === 'scanning' || status === 'granted') return;
    setStatus('scanning');
    setAttempts(prev => prev + 1);
    setAuthText('');

    // Type out verification text
    const verifyText = 'VERIFYING BIOMETRIC SIGNATURE...';
    let i = 0;
    const typeTimer = setInterval(() => {
      i++;
      setAuthText(verifyText.slice(0, i));
      if (i >= verifyText.length) clearInterval(typeTimer);
    }, 40);

    setTimeout(() => {
      clearInterval(typeTimer);
      if (code === CORRECT_CODE) {
        setStatus('granted');
        setAuthText('ACCESS GRANTED');
        setFlashScreen('green');
        setTimeout(() => setFlashScreen(null), 400);
        setMatrixRain(true);
        setTimeout(() => setMatrixRain(false), 2000);
        setTimeout(() => onComplete?.(), 2500);
      } else {
        setStatus('denied');
        setAuthText('ACCESS DENIED — INTRUDER ALERT');
        setFlashScreen('red');
        setTimeout(() => setFlashScreen(null), 300);
        setShaking(true);
        setTimeout(() => {
          setShaking(false);
          setCode('');
          setStatus('idle');
          setAuthText('');
        }, 1800);
      }
    }, 2200);
  }, [code, status, onComplete]);

  const digits = code.split('');
  const isReady = code.length === 6 && status === 'idle';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" data-testid="password-gate">
      {/* Background layers */}
      <div className="fixed inset-0 z-0">
        <img src={BG_IMG} alt="" className="w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-red-950/10 via-transparent to-red-950/10" />
      </div>

      <HexGrid />
      <FloatingParticles />
      <RedVignette />

      {/* CRT scanlines */}
      {!booting && (
        <div className="fixed inset-0 pointer-events-none z-[50]" style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, transparent 4px)',
        }} />
      )}

      {/* Boot Sequence */}
      <AnimatePresence>
        {booting && <BootSequence onComplete={handleBootComplete} />}
      </AnimatePresence>

      {/* Screen flash on grant/deny */}
      <AnimatePresence>
        {flashScreen && (
          <motion.div
            initial={{ opacity: flashScreen === 'green' ? 0.3 : 0.25 }}
            exit={{ opacity: 0 }}
            transition={{ duration: flashScreen === 'green' ? 0.4 : 0.3 }}
            className="fixed inset-0 z-40 pointer-events-none"
            style={{
              background: flashScreen === 'green'
                ? 'radial-gradient(circle, rgba(0,255,65,0.15), rgba(0,255,65,0.05) 70%)'
                : 'radial-gradient(circle, rgba(255,0,0,0.15), rgba(255,0,0,0.05) 70%)',
            }}
          />
        )}
      </AnimatePresence>

      {/* Matrix rain on grant */}
      <MatrixRain active={matrixRain} />

      {/* Green border pulse on grant */}
      {status === 'granted' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.8, 0] }}
          transition={{ duration: 1 }}
          className="fixed inset-0 pointer-events-none z-30"
          style={{ border: '2px solid rgba(0,255,65,0.5)', boxShadow: 'inset 0 0 60px rgba(0,255,65,0.1)' }}
        />
      )}

      {/* HUD overlay */}
      {!booting && (
        <>
          <SystemLog attempts={attempts} />
          <HUDCorners />

          {/* Bottom HUD */}
          <div className="fixed bottom-3 left-4 z-20 flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <Eye size={10} className="text-green-800" />
              <span className="text-xs font-terminal text-green-800 tracking-wider">{PASSWORD_GATE.hud.cam}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Wifi size={10} className="text-green-800" />
              <span className="text-xs font-terminal text-green-800 tracking-wider">{PASSWORD_GATE.hud.signal}</span>
              {/* Animated signal bars */}
              <div className="flex items-end gap-px">
                {[3, 5, 7, 9, 11].map((h, i) => (
                  <motion.div
                    key={i}
                    animate={{ opacity: [0.3, 0.8, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
                    className="rounded-sm"
                    style={{ width: 2, height: h, background: i < 4 ? 'rgba(0,255,65,0.5)' : 'rgba(0,255,65,0.2)' }}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="fixed bottom-3 right-4 z-20 flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <motion.div
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <Activity size={10} className="text-red-700" />
              </motion.div>
              <span className="text-xs font-terminal text-red-700 tracking-wider">{PASSWORD_GATE.hud.biohazard}</span>
            </div>
          </div>
        </>
      )}

      {/* Main terminal */}
      {!booting && (
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="relative z-10 w-full max-w-md"
        >
          <motion.div
            animate={shaking ? { x: [0, -8, 8, -6, 6, -3, 3, 0] } : {}}
            transition={{ duration: 0.5 }}
            className="terminal-panel relative overflow-hidden"
          >
            {/* Scanning bar overlay */}
            <ScanBar active={status === 'scanning'} />

            {/* Terminal Header */}
            <div className="relative h-8 flex items-center px-3 text-xs font-terminal tracking-wider">
              <div className="flex items-center gap-1.5 mr-2">
                <motion.div
                  animate={{ opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="w-2 h-2 rounded-full bg-red-500"
                  style={{ boxShadow: '0 0 4px rgba(255,0,0,0.5)' }}
                />
                <div className="w-2 h-2 rounded-full bg-yellow-500/40" />
                <div className="w-2 h-2 rounded-full bg-green-500/40" />
              </div>
              <span className="text-green-600">{PASSWORD_GATE.terminal.header}</span>
              <span className="ml-auto text-green-700">{PASSWORD_GATE.terminal.lockdown}</span>
            </div>

            <div className="p-6 pt-3 space-y-4">
              {/* Umbrella Logo with rotating glow ring */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ duration: 1, type: 'spring', stiffness: 80 }}
                className="flex justify-center"
              >
                <div className="relative">
                  <svg viewBox="0 0 140 140" className="absolute -inset-2.5 w-[calc(100%+20px)] h-[calc(100%+20px)] animate-spin-slow" style={{ animationDuration: '15s' }}>
                    <circle cx="70" cy="70" r="66" fill="none" stroke="rgba(204,0,0,0.15)" strokeWidth="1" strokeDasharray="8 12" />
                  </svg>
                  <svg viewBox="0 0 140 140" className="absolute -inset-2.5 w-[calc(100%+20px)] h-[calc(100%+20px)] animate-spin-slow" style={{ animationDuration: '25s', animationDirection: 'reverse' }}>
                    <circle cx="70" cy="70" r="62" fill="none" stroke="rgba(204,0,0,0.1)" strokeWidth="0.5" strokeDasharray="4 20" />
                  </svg>
                  <img src="/umbrella-corporation-logo.png" alt="Umbrella Corp" style={{ width: 100, height: 100 }} />
                  <motion.div
                    animate={{
                      boxShadow: ['0 0 30px rgba(204,0,0,0.2)', '0 0 50px rgba(204,0,0,0.35)', '0 0 30px rgba(204,0,0,0.2)'],
                    }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    className="absolute inset-0 rounded-full"
                  />
                </div>
              </motion.div>

              {/* Title */}
              <div className="text-center">
                <motion.h1
                  initial={{ letterSpacing: '20px', opacity: 0 }}
                  animate={{ letterSpacing: '8px', opacity: 1 }}
                  transition={{ duration: 1, delay: 0.3 }}
                  className="font-terminal text-2xl text-gray-200 uppercase"
                >
                  {PASSWORD_GATE.terminal.title1}
                </motion.h1>
                <motion.h1
                  initial={{ letterSpacing: '20px', opacity: 0 }}
                  animate={{ letterSpacing: '8px', opacity: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                  className="font-terminal text-xl text-gray-300 uppercase"
                >
                  {PASSWORD_GATE.terminal.title2}
                </motion.h1>
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="flex items-center justify-center gap-2 mt-2"
                >
                  <div className="w-10 h-px bg-gradient-to-r from-transparent to-red-800/60" />
                  <span className="text-xs font-terminal text-red-600/80 tracking-[4px] uppercase">{PASSWORD_GATE.terminal.classifiedAccess}</span>
                  <div className="w-10 h-px bg-gradient-to-l from-transparent to-red-800/60" />
                </motion.div>
              </div>

              {/* Fingerprint Scanner */}
              <FingerprintScanner status={status} />

              {/* Status + Auth text */}
              <div className="text-center space-y-1.5">
                <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-sm border transition-all duration-500
                  ${status === 'granted' ? 'border-green-500/50 bg-green-950/20' : status === 'denied' ? 'border-red-500/50 bg-red-950/20' : status === 'scanning' ? 'border-yellow-600/30 bg-yellow-950/10' : 'border-green-900/20 bg-black/20'}`}
                >
                  <motion.div
                    animate={status === 'scanning' ? { scale: [1, 1.4, 1], opacity: [1, 0.5, 1] } : {}}
                    transition={{ duration: 0.6, repeat: Infinity }}
                    className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${status === 'granted' ? 'bg-green-400' : status === 'denied' ? 'bg-red-400' : status === 'scanning' ? 'bg-yellow-500' : 'bg-green-600'}`}
                    style={{ boxShadow: status === 'granted' ? '0 0 8px rgba(0,255,65,0.6)' : status === 'denied' ? '0 0 8px rgba(255,0,0,0.6)' : 'none' }}
                  />
                  <span className={`text-xs font-terminal tracking-[3px] transition-colors duration-300 ${status === 'granted' ? 'text-green-400 text-glow-green' :
                      status === 'denied' ? 'text-red-400 text-glow-red' :
                        status === 'scanning' ? 'text-yellow-500' :
                          'text-green-600'}`}
                  >
                    {status === 'granted' ? PASSWORD_GATE.status.granted :
                      status === 'denied' ? PASSWORD_GATE.status.denied :
                        status === 'scanning' ? PASSWORD_GATE.status.scanning :
                          PASSWORD_GATE.status.idle}
                  </span>
                </div>
                {/* Typed auth text */}
                {authText && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`text-xs font-terminal tracking-wider ${status === 'granted' ? 'text-green-500' : status === 'denied' ? 'text-red-500' : 'text-yellow-600'
                      }`}
                  >
                    {authText}
                    {status === 'scanning' && (
                      <motion.span animate={{ opacity: [1, 0] }} transition={{ duration: 0.5, repeat: Infinity }}>_</motion.span>
                    )}
                  </motion.p>
                )}
              </div>

              {/* Code Display */}
              <div className="relative" data-testid="code-display">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-terminal text-green-800 tracking-wider uppercase flex items-center gap-1">
                    <Fingerprint size={9} className="text-green-700" />
                    {PASSWORD_GATE.terminal.securityCode}
                  </span>
                  <span className="text-xs font-terminal text-green-800 tabular-nums">{code.length}/6</span>
                </div>
                <div className={`bg-black/70 border rounded-sm p-4 flex justify-center gap-3 transition-all duration-500
                  ${status === 'granted' ? 'border-green-500/50 shadow-[0_0_20px_rgba(0,255,65,0.15)]' : status === 'denied' ? 'border-red-500/50 shadow-[0_0_20px_rgba(255,0,0,0.15)]' : 'border-green-900/30'}`}
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <motion.div
                      key={i}
                      animate={digits[i] ? { scale: [1, 1.2, 1] } : {}}
                      transition={{ duration: 0.15 }}
                      className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all duration-300 relative overflow-hidden
                        ${digits[i]
                          ? status === 'granted' ? 'border-green-400 bg-green-500/25' : status === 'denied' ? 'border-red-400 bg-red-500/25' : 'border-green-500 bg-green-500/15'
                          : i === code.length ? 'border-green-700/40 bg-green-950/10' : 'border-green-900/25 bg-transparent'}`}
                    >
                      {digits[i] && (
                        <>
                          {/* Brief digit flash then dot */}
                          {lastDigitFlash === i ? (
                            <motion.span
                              initial={{ scale: 0, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              className="font-terminal text-sm text-green-400 text-glow-green"
                            >
                              {digits[i]}
                            </motion.span>
                          ) : (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className={`w-3 h-3 rounded-full ${status === 'granted' ? 'bg-green-400' : status === 'denied' ? 'bg-red-400' : 'bg-green-400'}`}
                              style={{ boxShadow: `0 0 8px ${status === 'denied' ? 'rgba(255,0,0,0.6)' : 'rgba(0,255,65,0.6)'}` }}
                            />
                          )}
                        </>
                      )}
                      {/* Next-to-fill indicator */}
                      {!digits[i] && i === code.length && status === 'idle' && (
                        <motion.div
                          animate={{ opacity: [0.2, 0.5, 0.2] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          className="w-1.5 h-1.5 rounded-full bg-green-600"
                        />
                      )}
                    </motion.div>
                  ))}
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-[3px] bg-green-900/20 rounded-full overflow-hidden">
                  <motion.div
                    animate={{ width: `${(code.length / 6) * 100}%` }}
                    transition={{ duration: 0.2 }}
                    className={`h-full rounded-full ${status === 'granted' ? 'bg-green-400' : status === 'denied' ? 'bg-red-500' : 'bg-gradient-to-r from-green-600 to-green-400'}`}
                    style={{ boxShadow: status === 'granted' ? '0 0 8px rgba(0,255,65,0.4)' : 'none' }}
                  />
                </div>
              </div>

              {/* Keypad */}
              <div className="relative" ref={keypadRef}>
                {/* Ripple effect */}
                <AnimatePresence>
                  {ripple && <KeyRipple key={ripple.id} x={ripple.x} y={ripple.y} active />}
                </AnimatePresence>

                {/* Scan line across keypad */}
                {status === 'idle' && (
                  <motion.div
                    animate={{ top: ['0%', '100%'] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                    className="absolute left-0 right-0 h-px z-5 pointer-events-none"
                    style={{ background: 'linear-gradient(90deg, transparent, rgba(0,255,65,0.08), transparent)' }}
                  />
                )}

                <div className="grid grid-cols-3 gap-2 max-w-[210px] mx-auto" data-testid="keypad">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                    <motion.button
                      key={n}
                      whileHover={{ scale: 1.06, borderColor: 'rgba(0,255,65,0.5)' }}
                      whileTap={{ scale: 0.92, background: 'linear-gradient(180deg, #0a1a0a 0%, #061006 100%)' }}
                      className="keypad-btn-enhanced"
                      onClick={(e) => handleKey(String(n), e)}
                      data-testid={`key-${n}`}
                    >
                      {n}
                    </motion.button>
                  ))}
                  <motion.button
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.92 }}
                    className="keypad-btn-enhanced !text-red-500 !border-red-900/30"
                    onClick={handleDelete}
                    data-testid="key-delete"
                  >
                    <Delete size={18} />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.06, borderColor: 'rgba(0,255,65,0.5)' }}
                    whileTap={{ scale: 0.92, background: 'linear-gradient(180deg, #0a1a0a 0%, #061006 100%)' }}
                    className="keypad-btn-enhanced"
                    onClick={(e) => handleKey('0', e)}
                    data-testid="key-0"
                  >
                    0
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.92 }}
                    className="keypad-btn-enhanced !text-yellow-500 !border-yellow-900/30"
                    onClick={() => setCode('')}
                    data-testid="key-clear"
                  >
                    C
                  </motion.button>
                </div>
              </div>

              {/* Authenticate Button */}
              <motion.button
                whileHover={isReady ? { scale: 1.02, boxShadow: '0 0 30px rgba(204,0,0,0.35)' } : {}}
                whileTap={isReady ? { scale: 0.98 } : {}}
                onClick={handleSubmit}
                disabled={!isReady}
                className={`w-full py-3.5 font-terminal text-lg tracking-[5px] uppercase rounded-sm transition-all duration-300 flex items-center justify-center gap-3 relative overflow-hidden
                  ${isReady
                    ? 'bg-gradient-to-r from-red-900/80 via-red-800/80 to-red-900/80 border-2 border-red-500/70 text-red-200 text-glow-red cursor-pointer'
                    : 'bg-gray-900/40 border-2 border-gray-800 text-gray-600 cursor-not-allowed'}`}
                data-testid="key-submit"
              >
                {isReady && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-red-500/10 to-transparent"
                    animate={{ x: ['-100%', '200%'] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                  />
                )}
                <Fingerprint size={16} className="relative z-10" />
                <span className="relative z-10">{PASSWORD_GATE.terminal.authenticate}</span>
                <Zap size={16} className="relative z-10" />
              </motion.button>

              {/* Stats Bar */}
              <div className="grid grid-cols-3 gap-px bg-green-900/10 rounded-sm overflow-hidden">
                {[
                  { label: 'Attempts', value: String(attempts).padStart(3, '0'), color: attempts >= 3 ? 'text-red-500' : 'text-green-500' },
                  { label: 'Protocol', value: 'LAMBDA', color: 'text-green-500' },
                  { label: 'Threat', value: attempts >= 3 ? 'CRITICAL' : 'MAXIMUM', color: 'text-red-500' },
                ].map(stat => (
                  <div key={stat.label} className="bg-black/40 p-2 text-center">
                    <div className="text-xs font-terminal text-green-800 tracking-wider uppercase">{stat.label}</div>
                    <motion.div
                      key={stat.value}
                      initial={stat.label === 'Attempts' && attempts > 0 ? { scale: 1.3, color: '#ef4444' } : false}
                      animate={{ scale: 1 }}
                      className={`text-xs font-terminal ${stat.color}`}
                    >
                      {stat.value}
                    </motion.div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-center gap-2">
                <AlertTriangle size={9} className="text-red-900/60" />
                <span className="text-xs font-terminal text-green-900 tracking-[2px] uppercase">{PASSWORD_GATE.footer}</span>
                <AlertTriangle size={9} className="text-red-900/60" />
              </div>
            </div>
          </motion.div>

          {/* Bottom label */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.2 }}
            transition={{ delay: 1.2 }}
            className="text-center mt-3 text-xs font-terminal tracking-[3px] text-gray-700 uppercase"
          >
            {PASSWORD_GATE.bottomLabel}
          </motion.p>
        </motion.div>
      )}


    </div>
  );
}
